import { expose } from "threads/worker"

//import '@tensorflow/tfjs-backend-cpu';
//import {SemanticSegmentation} from "@tensorflow-models/deeplab";
//import * as tf from '@tensorflow/tfjs';
//import * as deeplab from "@tensorflow-models/deeplab";
//import '@tensorflow/tfjs-backend-webgl';
//import {DeepLabInput, ModelArchitecture, QuantizationBytes} from "@tensorflow-models/deeplab/dist/types";

import {Mat, Point} from "mirada";
import {CBARImaging, Labels} from "../CBARImaging";
import {
    deserializeImage, fastDilateErode,
    getMatrixType, ImageBuffer, InputShape, serializeImage,
} from "../Utils";
import {loadOpenCV} from "./CBARWorkersCommon";

import {Scalar} from "../../components";

// tf.ENV.registerFlag('HAS_WEBGL', ()=>1);
// tf.ENV.registerFlag('WEBGL_VERSION', ()=>1);
// //tf.ENV.backend = 'webgl';
// tf.setBackend('webgl');

// video buffer

const DEBUG_SEED_POINT = true;

export type SegmentationInput = {
    seedPoint:Point,
    image:InputShape,
    edgesImage:InputShape,
}

export type SegmentationResult = {
    mask:ImageBuffer
    roi:{x:number, y:number, width:number, height:number},
    lightingFactor:number,
    lightingOffset:number,
    debugImage?:ImageBuffer|undefined
}

loadOpenCV(true);

export const getIntensity = (color:Scalar|Float64Array|number) => {
    if (typeof color === 'number') {
        return color
    }
    return color.length > 1 ? color[1] : color[0];
}

class _CBARSegmenter {

    //private _model?:SemanticSegmentation;

    private _lsd?:any;

    async initialize() {
        // console.log("counter.loadModel called, awaiting result")
        // let modelName:ModelArchitecture = 'ade20k';
        // let quantizationBytes:QuantizationBytes = 2;
        // this._model = await deeplab.load({base: modelName, quantizationBytes});

        //console.log("_CBARSegmenter is initialized");
        ///await onOpenCvReady();

        console.log("initialized _CBARSegmenter");
    }

    async destroy() {
        //this._mask?.delete();
    }

    private getEdgeLines(mask:Mat, pos:number, horizontal=false, minLength=5) {

        let on = false;
        let start = 0;
        let stop = 0;

        const stopIndex = horizontal ? mask.cols : mask.rows;
        const startStops:Array<[number, number]> = []

        for (let i=0; i<stopIndex; i++) {
            const sample = horizontal ? mask.ucharPtr(pos, i)[0] : mask.ucharPtr(i, pos)[0];
            if (sample) {
                if (!on) {
                    start = i;
                }
                on = true;
            } else if (on) {
                if (stop - start > minLength) {
                    startStops.push([start, stop]);
                }
                on = false;
            }

            if (on) {
                stop = i;
            }
        }

        if (stop - start > minLength) {
            startStops.push([start, stop]);
        }

        return startStops;
    }

    private extendMask(mask:Mat, padding:number) {

        const maskExpanded = cv.Mat.zeros(mask.rows + 2 * padding, mask.cols + 2 * padding, cv.CV_8UC1);

        let roi = maskExpanded.roi({x:padding, y:padding, width:mask.cols, height:mask.rows});
        mask.copyTo(roi);
        roi.delete();

        const testDistance = 5;

        //top edge (works)
        this.getEdgeLines(mask, testDistance, true).forEach(startStop=>{
            cv.rectangle(maskExpanded,
                {x:startStop[0] <= testDistance ? 0 : startStop[0] + padding, y:0},
                {x:startStop[1] >= mask.cols - testDistance ? mask.cols + 2 * padding : startStop[1] + padding, y:padding},
                [255,255,255,255], cv.FILLED);
        });

        //right edge
        this.getEdgeLines(mask, mask.cols - testDistance - 1).forEach(startStop=>{
            cv.rectangle(maskExpanded,
                {x:mask.cols + padding, y:startStop[0] <= testDistance ? 0 : padding + startStop[0]},
                {x:mask.cols + 2 * padding, y:startStop[1] >= mask.rows - testDistance ? mask.rows + 2 * padding : padding + startStop[1]},
                [255,255,255,255], cv.FILLED);
        });

        //bottom edge
        this.getEdgeLines(mask, mask.rows - testDistance - 1, true).forEach(startStop=>{
            cv.rectangle(maskExpanded,
                {x:padding + mask.rows - 1, y:startStop[0] <= testDistance ? 0 : padding + startStop[0]},
                {x:2 * padding + mask.rows - 1, y:startStop[1] >= mask.cols - testDistance ? mask.rows + padding * 2 : padding + startStop[1]},
                [255,255,255,255], cv.FILLED);
        });

        //left edge (works)
        this.getEdgeLines(mask, testDistance).forEach(startStop=>{
            cv.rectangle(maskExpanded,
                {x:0, y:startStop[0] < testDistance ? 0 : padding + startStop[0]},
                {x:padding, y:startStop[1] >= mask.cols - testDistance ? mask.cols - 1: padding + startStop[1]},
                [255,255,255,255], cv.FILLED);
        });

        return maskExpanded;
    }

    private segmentAtPoint(img:Mat, edges:Mat, seedPoint:Point) {
        const maskPadded = new cv.Mat(img.rows + 2, img.cols + 2, cv.CV_8UC1, [0, 0, 0, 0])
        const rect = {};

        const lowerThreshold = [3, 3, 3, 255];
        const upperThreshold = [3, 3, 3, 255];

        cv.floodFill(
            img,
            maskPadded,
            seedPoint,
            [255, 255, 255, 255],
            rect,
            lowerThreshold,
            upperThreshold,
            4 | (255 << 8));

        let roi = new cv.Rect(1, 1, img.cols, img.rows);

        const mask = new cv.Mat(img.rows, img.cols, cv.CV_8U, [0, 0, 0, 0])
        const crop = maskPadded.roi(roi);
        crop.copyTo(mask);
        crop.delete();
        maskPadded.delete();

        //get rid of small holes
        // const M = cv.Mat.ones(5, 5, cv.CV_8U);
        // cv.dilate(mask, mask, M);
        // cv.erode(mask, mask, M);
        // M.delete();

        return mask;
    }

    private getLighting(background:Mat, edges:Mat, mask:Mat) {

        fastDilateErode(mask, cv.erode, 5, 0.25);
        fastDilateErode(edges, cv.dilate, 5, 0.25);

        edges.setTo([0,0,0,0], mask);

        let insideM = new cv.Mat();
        let insideS = new cv.Mat();

        cv.meanStdDev(background, insideM, insideS, edges);

        const backgroundMeanIntensity = getIntensity(insideM.data64F) / 255.0;
        const backgroundStdDevIntensity = getIntensity(insideS.data64F) / 255.0;
        const imageLuminance = 1.1 * (backgroundMeanIntensity + backgroundStdDevIntensity); //one sigma above mean

        cv.meanStdDev(background, insideM, insideS, mask);

        const maskedMeanIntensity = getIntensity(insideM.data64F) / 255.0;
        //const maskedStdDevIntensity = getIntensity(insideS.data64F) / 255.0;
        const maskLuminance = maskedMeanIntensity; //one sigma above mean

        insideS.delete();
        insideM.delete();

        const maxValue = 2.2;

        const lightingFactor = imageLuminance / maskLuminance;
        const lightingOffset = Math.max(lightingFactor - maxValue, 0.0) * 255.0;

        //console.log(`image luminance ${imageLuminance}, masked: ${maskLuminance}`);

        return {lightingFactor, lightingOffset};
    }

    private refineMask(img:Mat, mask:Mat, edges:Mat) {

        fastDilateErode(mask, cv.dilate, 5, 0.25);

        // const changesCertain = new cv.Mat();
        // cv.distanceTransform(mask, changesCertain, cv.DIST_L2, cv.DIST_MASK_5);
        // cv.normalize(changesCertain, changesCertain, 255.0, 0, cv.NORM_MINMAX);
        // cv.threshold(changesCertain, changesCertain,  255.0 - 255.0 * distance, 255, cv.THRESH_BINARY);
        // changesCertain.convertTo(changesCertain, cv.CV_8U);

        const markers = cv.Mat.zeros(mask.rows, mask.cols, cv.CV_32S);

        markers.setTo(CBARImaging.nonMaskLabel);
        markers.setTo(CBARImaging.maskLabel, mask);

        const contoursResult = new cv.MatVector();
        const hierarchy = new cv.Mat();
        cv.findContours(mask, contoursResult, hierarchy, cv.RETR_EXTERNAL, cv.CHAIN_APPROX_NONE);
        const thickness = Math.ceil(mask.cols / 20);
        //console.log("thickness", thickness);
        cv.drawContours(markers, contoursResult, -1, CBARImaging.uncertainLabel, thickness);
        contoursResult.delete();
        hierarchy.delete();

        markers.setTo(CBARImaging.uncertainLabel, edges);

        let rgb = img;
        if (img.channels() === 1) {
            rgb = new cv.Mat();
            cv.cvtColor(img, rgb, cv.COLOR_GRAY2RGB);
        }

        cv.watershed(rgb, markers);

        if (img !== rgb) {
            rgb.delete();
        }

        markers.convertTo(mask, cv.CV_8U, 1, 0);
        markers.delete();

        cv.threshold(mask, mask, Labels.mask - 1, 0, cv.THRESH_TOZERO);

        const kernel = cv.Mat.ones(3, 3, cv.CV_8U);
        cv.dilate(mask, mask, kernel);
        kernel.delete();

        return mask;
    }

    public segmentationInput?:SegmentationInput;

    //online js demo of edges stuff https://observablehq.com/@hellonearthis/opencv-js-hough-line-transform
    fastSegment(imageData:ArrayBuffer, edgesData:ArrayBuffer) : SegmentationResult|void {
        if (!this.segmentationInput) return;

        let debugImage:Mat|undefined;
        let start = performance.now();

        let img = deserializeImage({...this.segmentationInput.image, data:imageData});
        let edges = deserializeImage({...this.segmentationInput.edgesImage, data:edgesData});

        //smooth inputs:
        // median blur took 256.69999998807907
        // start = performance.now();
        // cv.medianBlur(img, img, 5);
        // console.log(`median blur took ${performance.now() - start}`);

        const segImage = img.clone();

        // CBARSegmenter.ts:310 findLines took 96.8999999910593
        // start = performance.now();
        const edgesScale = img.cols / edges.cols;
        const lines = new cv.Mat();
        cv.HoughLinesP(edges, lines, 1, 2.5 * Math.PI/180, 30, 40, 20);
        cv.resize(edges, edges, img.size(), 0,0, cv.INTER_NEAREST);

        for (let i = 0; i < lines.rows; ++i) {
            let startPoint = new cv.Point(lines.data32S[i * 4] * edgesScale, lines.data32S[i * 4 + 1] * edgesScale);
            let endPoint = new cv.Point(lines.data32S[i * 4 + 2] * edgesScale, lines.data32S[i * 4 + 3] * edgesScale);
            cv.line(edges, startPoint, endPoint, [255, 255, 255, 255], 2);
        }
        //console.log(`find/draw lines took ${performance.now() - start}`);
        lines.delete();

        segImage.setTo([0,255,0,255], edges);

        //debugImage = segImage.clone();

        let seedPoint = this.segmentationInput.seedPoint;
        seedPoint.x = Math.max(Math.min(seedPoint.x, img.cols - 1), 0);
        seedPoint.y = Math.max(Math.min(seedPoint.y, img.rows - 1), 0);

        // CBARSegmenter.ts:333 segmentAtPoint took 25
        //start = performance.now();
        let mask = this.segmentAtPoint(segImage, edges, seedPoint);
        segImage.delete();
        //console.log(`segmentAtPoint took ${performance.now() - start}`);

        // CBARSegmenter.ts:337 refineMask took 519.4000000059605
        //start = performance.now();

        const M = cv.Mat.ones(3, 3, cv.CV_8U);
        cv.dilate(edges, edges, M);
        M.delete();

        this.refineMask(img, mask, edges);
        //console.log(`refineMask took ${performance.now() - start}`);

        //start = performance.now();
        // CBARSegmenter.ts:342 extendMask took 4.399999991059303
        const padding = 10; //Math.ceil(mask.cols / 10);
        const extendedMask = this.extendMask(mask, padding);
        //console.log(`extendMask took ${performance.now() - start}`);

        // CBARSegmenter.ts:346 getLighting took 1404.5999999940395
        //start = performance.now();
        const lighting = this.getLighting(img, edges, mask);
        //console.log(`getLighting took ${performance.now() - start}`);

        // GaussianBlur took 119.6000000089407
        //start = performance.now();
        const blur = Math.ceil(mask.cols / 200) * 2 + 1;
        cv.blur(extendedMask, extendedMask, new cv.Size(blur,blur), {x:-1, y:-1}, cv.BORDER_DEFAULT);
        //console.log(`cv.blur took ${performance.now() - start}`);

        edges.delete();
        mask.delete();
        img.delete();

        //const resultData = new Uint8Array(extendedMask.data);

        const result = {
            mask:serializeImage(extendedMask),
            debugImage:debugImage ? serializeImage(debugImage) : undefined,
            roi:{x:padding, y:padding, width:extendedMask.cols - padding * 2, height:extendedMask.rows - padding * 2},
            lightingFactor:lighting.lightingFactor,
            lightingOffset:lighting.lightingOffset,
        }

        debugImage?.delete();
        extendedMask.delete();

        return result;
    }
}

let _instance:_CBARSegmenter|undefined;

const segmenter = {
    // quantizationBytes either 1, 2 or 4 (disabled)
    async initialize() {
        _instance = new _CBARSegmenter();
        await _instance.initialize();
    },

    async destroy() {
        await _instance?.destroy();
    },

    // segment(params:_SegmentationInput) : SegmentationResult {
    //     return _instance!.segment(params);
    // },

    configureInput(shape:SegmentationInput) {
        //console.log("set image size", size.width, size.height);
        _instance!.segmentationInput = shape;
    },

    fastSegment(data:ArrayBuffer, edgesData:ArrayBuffer) {
        return _instance!.fastSegment(data, edgesData);
    }
}

export type CBARSegmenter = typeof segmenter

expose(segmenter)

// if (!_model) return -1;
// let image:DeepLabInput|undefined;
//
// if (data instanceof ArrayBuffer) {
//     image = new ImageData(new Uint8ClampedArray( data ), width, height);
// } else if (data instanceof OffscreenCanvas) {
//     //image = data.transferToImageBitmap();
// }
//
// if (!image) return 0;
//
// const result = _model.segment(image);
// console.log("segmenter.segment");
//
// return 9;

// let pixels = new ImageData( width, height );
// pixels.data.set( new Uint8ClampedArray( data ) );
// const result = await _model.segment(pixels);
// console.log("segmenter.segment finished");
// return result.segmentationMap;